// src/components/admin/attendance/HolidayPlanManagement.js
import React, { useState, useEffect, useMemo } from 'react';
import {
    Plus, Search, Calendar, Globe, Users, Download, Upload,
    Check, X, Edit2, Trash2, Filter, ChevronDown, ChevronUp,
    Clock, CheckCircle, XCircle, AlertCircle, Copy, RefreshCw
} from 'lucide-react';
import holidayPlanService from '../../../services/holidayPlan.service';
import { useEmployees } from '../../../contexts/EmployeeContext';
import { countries } from '../../../data/countries';
import * as XLSX from 'xlsx';
import { formatISTDate, toISTDateString, getISTComponents } from '../../../utils/dateHelpers';

const HolidayPlanManagement = () => {
    // State management
    const [holidayPlans, setHolidayPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEmployeeModal, setShowEmployeeModal] = useState(false);
    const [loading, setLoading] = useState(false);

    // Optional Holiday Requests state
    const [optionalHolidayRequests, setOptionalHolidayRequests] = useState([]);
    const [requestsFilter, setRequestsFilter] = useState('pending');
    const [showRequestsPanel, setShowRequestsPanel] = useState(false);
    const [processingRequestId, setProcessingRequestId] = useState(null);

    // Create plan states
    const [planName, setPlanName] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [countryHolidays, setCountryHolidays] = useState([]);
    const [selectedHolidays, setSelectedHolidays] = useState([]);

    // Plan details - available holidays for selected year (for adding to existing plan)
    const [availableHolidaysForYear, setAvailableHolidaysForYear] = useState([]);

    // Multi-year support states
    const [planYears, setPlanYears] = useState([]);  // Years that have holidays in the plan
    const [selectedPlanYear, setSelectedPlanYear] = useState(null);  // Currently selected year tab
    const [showCreateYearModal, setShowCreateYearModal] = useState(false);
    const [newYearToCreate, setNewYearToCreate] = useState(new Date().getFullYear() + 1);

    // Employee assignment states
    const { employees } = useEmployees();
    const holidayEmployeeCounts = useMemo(() => {
        return employees.reduce((counts, employee) => {
            const planId = employee.holiday_plan_id
                ?? employee.holiday_list
                ?? employee.holidayList;

            if (!planId) {
                return counts;
            }

            const key = String(planId);
            counts[key] = (counts[key] || 0) + 1;
            return counts;
        }, {});
    }, [employees]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [employeeFilters, setEmployeeFilters] = useState({
        department: '',
        location: '',
        legalEntity: '',
        shift: ''
    });

    // Load holiday plans on mount
    useEffect(() => {
        fetchHolidayPlans();
        fetchOptionalHolidayRequests();
    }, []);

    // Fetch optional holiday requests when filter changes
    useEffect(() => {
        fetchOptionalHolidayRequests();
    }, [requestsFilter]);

    const fetchOptionalHolidayRequests = async () => {
        try {
            const requests = await holidayPlanService.getOptionalHolidayRequests(
                requestsFilter === 'all' ? null : requestsFilter
            );
            setOptionalHolidayRequests(requests);
        } catch (error) {
            console.error('Error fetching optional holiday requests:', error);
        }
    };

    const handleApproveRequest = async (requestId) => {
        try {
            setProcessingRequestId(requestId);
            await holidayPlanService.approveOptionalHoliday(requestId);
            await fetchOptionalHolidayRequests();
        } catch (error) {
            console.error('Error approving request:', error);
            alert('Failed to approve request');
        } finally {
            setProcessingRequestId(null);
        }
    };

    const handleRejectRequest = async (requestId, reason = null) => {
        try {
            setProcessingRequestId(requestId);
            const rejectReason = reason || window.prompt('Enter rejection reason (optional):');
            await holidayPlanService.rejectOptionalHoliday(requestId, rejectReason);
            await fetchOptionalHolidayRequests();
        } catch (error) {
            console.error('Error rejecting request:', error);
            alert('Failed to reject request');
        } finally {
            setProcessingRequestId(null);
        }
    };

    const fetchHolidayPlans = async () => {
        try {
            setLoading(true);
            const plans = await holidayPlanService.getHolidayPlans();
            setHolidayPlans(plans);
        } catch (error) {
            console.error('Error fetching plans:', error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch country holidays when country is selected
    const handleCountrySelect = async (country) => {
        setSelectedCountry(country);
        setCountryHolidays([]);

        try {
            setLoading(true);
            const holidays = await holidayPlanService.fetchCountryHolidays(country.code, selectedYear);
            setCountryHolidays(holidays);
        } catch (error) {
            console.error('Error fetching country holidays:', error);
            // Don't show alert, just log - user can add manually
        } finally {
            setLoading(false);
        }
    };

    // Handle year change in Create Modal - re-fetch holidays for selected year
    const handleYearChange = async (year) => {
        setSelectedYear(year);
        setSelectedHolidays([]); // Reset selections when year changes

        if (selectedCountry) {
            try {
                setLoading(true);
                setCountryHolidays([]);
                const holidays = await holidayPlanService.fetchCountryHolidays(selectedCountry.code, year);
                setCountryHolidays(holidays);
            } catch (error) {
                console.error('Error fetching holidays for year:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle year tab change - just filters, does NOT overwrite dates
    const handlePlanYearChange = async (year) => {
        setSelectedPlanYear(year);
        setAvailableHolidaysForYear([]);

        if (selectedPlan) {
            try {
                setLoading(true);
                // Fetch holidays for the selected year only
                const plan = await holidayPlanService.getHolidayPlanById(selectedPlan.id, year);
                setSelectedPlan(plan);
            } catch (error) {
                console.error('Error fetching holidays for year:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    // Fetch available years for a plan
    const fetchPlanYears = async (planId) => {
        try {
            const years = await holidayPlanService.getYearsForPlan(planId);
            setPlanYears(years);
            return years;
        } catch (error) {
            console.error('Error fetching plan years:', error);
            return [];
        }
    };

    // Create holidays for a new year
    const handleCreateYearHolidays = async (method) => {
        if (!selectedPlan) return;

        try {
            setLoading(true);

            if (method === 'copy') {
                // Copy from the currently selected year (or plan year, or most recent if not set)
                const sourceYear = selectedPlanYear || selectedPlan?.year || (planYears.length > 0 ? Math.max(...planYears) : null);
                if (!sourceYear) {
                    alert('No source year available to copy from');
                    setLoading(false);
                    return;
                }
                await holidayPlanService.copyHolidaysToYear(selectedPlan.id, sourceYear, newYearToCreate);

                // Refresh years and switch to new year
                await fetchPlanYears(selectedPlan.id);
                setSelectedPlanYear(newYearToCreate);
                await handlePlanYearChange(newYearToCreate);
            } else if (method === 'fetch') {
                // Fetch from API and add
                const holidays = await holidayPlanService.fetchCountryHolidays(selectedPlan.country_code, newYearToCreate);
                if (holidays.length > 0) {
                    await holidayPlanService.addHolidaysForYear(selectedPlan.id, newYearToCreate, holidays);

                    // Refresh years and switch to new year
                    await fetchPlanYears(selectedPlan.id);
                    setSelectedPlanYear(newYearToCreate);
                    await handlePlanYearChange(newYearToCreate);
                } else {
                    alert('No holidays found for this country/year. Use "Add Holiday" button to add holidays manually.');
                }
            } else if (method === 'empty') {
                // For empty, just close modal and inform user to add holidays manually
                alert(`To add holidays for ${newYearToCreate}, use the "Add Holiday" button and select dates in that year.`);
            }

            setShowCreateYearModal(false);
        } catch (error) {
            console.error('Error creating year holidays:', error);
            alert('Failed to create holidays for the new year');
        } finally {
            setLoading(false);
        }
    };

    // Delete all holidays for a year
    const handleDeleteYear = async (year) => {
        if (!selectedPlan) return;
        if (!window.confirm(`Delete all holidays for ${year}? This cannot be undone.`)) return;

        try {
            setLoading(true);
            await holidayPlanService.deleteHolidaysForYear(selectedPlan.id, year);

            // Refresh years and switch to another year
            const updatedYears = await fetchPlanYears(selectedPlan.id);
            if (updatedYears.length > 0) {
                const newSelectedYear = updatedYears[0];
                setSelectedPlanYear(newSelectedYear);
                await handlePlanYearChange(newSelectedYear);
            } else {
                setSelectedPlan(prev => ({ ...prev, holidays: [] }));
            }
        } catch (error) {
            console.error('Error deleting year holidays:', error);
            alert('Failed to delete holidays for the year');
        } finally {
            setLoading(false);
        }
    };

    // Handle holiday selection
    const toggleHolidaySelection = (holiday) => {
        setSelectedHolidays(prev => {
            const exists = prev.find(h => h.date === holiday.date);
            if (exists) {
                return prev.filter(h => h.date !== holiday.date);
            } else {
                return [...prev, holiday];
            }
        });
    };

    // Create holiday plan
    const handleCreatePlan = async () => {
        if (!planName || !selectedCountry || selectedHolidays.length === 0) {
            alert('Please fill in all fields and select at least one holiday');
            return;
        }

        try {
            setLoading(true);
            const planData = {
                name: planName,
                countryCode: selectedCountry.code,
                countryName: selectedCountry.name,
                year: selectedYear,
                holidays: selectedHolidays
            };

            await holidayPlanService.createHolidayPlan(planData);

            // Reset and refresh
            setPlanName('');
            setSelectedCountry(null);
            setCountryHolidays([]);
            setSelectedHolidays([]);
            setShowCreateModal(false);
            fetchHolidayPlans();
        } catch (error) {
            console.error('Error creating plan:', error);
            alert('Failed to create holiday plan');
        } finally {
            setLoading(false);
        }
    };

    // View plan details
    const handleViewPlan = async (planId, year = null) => {
        try {
            setLoading(true);

            // Fetch available years for this plan
            const years = await fetchPlanYears(planId);

            // Determine which year to show
            let yearToShow = year;
            if (!yearToShow && years.length > 0) {
                // Default to current year if available, otherwise most recent
                const currentYear = new Date().getFullYear();
                yearToShow = years.includes(currentYear) ? currentYear : Math.max(...years);
            }
            setSelectedPlanYear(yearToShow);

            // Fetch plan with year filter
            const plan = await holidayPlanService.getHolidayPlanById(planId, yearToShow);
            setSelectedPlan(plan);
            setSelectedEmployees(plan.employees?.map(e => e.id) || []);
        } catch (error) {
            console.error('Error fetching plan details:', error);
        } finally {
            setLoading(false);
        }
    };

    // Add holiday to existing plan
    const handleAddHoliday = async (holidayData) => {
        if (!selectedPlan) return;

        try {
            await holidayPlanService.addHolidayToPlan(selectedPlan.id, holidayData);
            handleViewPlan(selectedPlan.id); // Refresh
        } catch (error) {
            console.error('Error adding holiday:', error);
            alert('Failed to add holiday');
        }
    };

    // Update holiday
    const handleUpdateHoliday = async (holidayId, updates) => {
        try {
            console.log('Updating holiday:', holidayId, 'with:', updates);
            const result = await holidayPlanService.updateHoliday(holidayId, updates);
            console.log('Update result:', result);
            await handleViewPlan(selectedPlan.id); // Refresh
        } catch (error) {
            console.error('Error updating holiday:', error);
            alert('Failed to update holiday. Please try again.');
        }
    };

    // Delete holiday
    const handleDeleteHoliday = async (holidayId) => {
        if (!window.confirm('Delete this holiday?')) return;

        try {
            await holidayPlanService.deleteHoliday(holidayId);
            handleViewPlan(selectedPlan.id); // Refresh
        } catch (error) {
            console.error('Error deleting holiday:', error);
        }
    };

    // Assign employees to plan
    const handleAssignEmployees = async () => {
        if (!selectedPlan) return;

        try {
            setLoading(true);
            await holidayPlanService.assignEmployees(selectedPlan.id, selectedEmployees);
            setShowEmployeeModal(false);
            handleViewPlan(selectedPlan.id); // Refresh
        } catch (error) {
            console.error('Error assigning employees:', error);
            alert('Failed to assign employees');
        } finally {
            setLoading(false);
        }
    };

    // Excel import
    const handleExcelImport = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const workbook = XLSX.read(event.target.result, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const data = XLSX.utils.sheet_to_json(worksheet);

                // Map Excel data to holiday format
                const holidays = data.map(row => ({
                    name: row['Holiday Name'] || row.name,
                    date: row['Date'] || row.date,
                    isOptional: row['Optional'] === 'Yes' || row.isOptional,
                    isSpecial: row['Special'] === 'Yes' || row.isSpecial,
                    description: row['Description'] || row.description || ''
                }));

                setSelectedHolidays(prev => [...prev, ...holidays]);
                alert(`Imported ${holidays.length} holidays from Excel`);
            } catch (error) {
                console.error('Excel import error:', error);
                alert('Failed to import Excel file');
            }
        };
        reader.readAsBinaryString(file);
    };

    // Filter employees
    const filteredEmployees = employees.filter(emp => {
        const matchesDept = !employeeFilters.department || emp.department === employeeFilters.department;
        const matchesLocation = !employeeFilters.location || emp.work_location === employeeFilters.location;
        return matchesDept && matchesLocation;
    });

    // Filtered plans for search
    const filteredPlans = holidayPlans.filter(plan =>
        plan.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        plan.country_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i);

    const pendingRequestsCount = optionalHolidayRequests.filter(r => r.status === 'pending').length;

    return (
        <div className="p-6">
            {/* Optional Holiday Requests Banner - Directs to Approvals */}
            {pendingRequestsCount > 0 && (
                <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                        <span className="text-amber-800 dark:text-amber-300 font-medium">
                            {pendingRequestsCount} pending optional holiday request{pendingRequestsCount > 1 ? 's' : ''} awaiting review
                        </span>
                    </div>
                    <span className="text-sm text-amber-700 dark:text-amber-400">
                        Go to Approvals → Other Approvals to review
                    </span>
                </div>
            )}

            <div className="grid grid-cols-3 gap-6">
                {/* Left Panel - Holiday Plans List */}
                <div className="col-span-1 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Holiday Plans</h2>
                    </div>

                    {/* Search */}
                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <input
                            type="text"
                            placeholder="Search plans..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    {/* Plans List */}
                    <div className="space-y-2 mb-4 max-h-96 overflow-y-auto">
                        {filteredPlans.map(plan => (
                            <div
                                key={plan.id}
                                onClick={() => handleViewPlan(plan.id)}
                                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                    selectedPlan?.id === plan.id
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                        : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <h3 className="font-medium text-sm text-gray-900 dark:text-white">{plan.name}</h3>
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                            {countries.find(c => c.code === plan.country_code)?.flag} {plan.country_name} • {plan.year}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {plan.holiday_count} holidays
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <Users className="w-3 h-3" />
                                        {holidayEmployeeCounts[String(plan.id)] ?? plan.employee_count ?? 0} employees
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* New Plan Button */}
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        New Holiday Plan
                    </button>
                </div>

                {/* Right Panel - Plan Details or Empty State */}
                <div className="col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    {selectedPlan ? (
                        <PlanDetailsView
                            plan={selectedPlan}
                            planYears={planYears}
                            selectedYear={selectedPlanYear}
                            onYearChange={handlePlanYearChange}
                            onAddYear={() => {
                                // Set default new year to next year not yet created
                                const existingYears = new Set(planYears);
                                let nextYear = new Date().getFullYear();
                                while (existingYears.has(nextYear)) {
                                    nextYear++;
                                }
                                setNewYearToCreate(nextYear);
                                setShowCreateYearModal(true);
                            }}
                            onDeleteYear={handleDeleteYear}
                            availableHolidaysForYear={availableHolidaysForYear}
                            onAddHoliday={handleAddHoliday}
                            onUpdateHoliday={handleUpdateHoliday}
                            onDeleteHoliday={handleDeleteHoliday}
                            onAssignEmployees={() => setShowEmployeeModal(true)}
                            onExcelImport={handleExcelImport}
                            loading={loading}
                        />
                    ) : (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-20">
                            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                            <p className="text-gray-700 dark:text-gray-300">Select a holiday plan to view details</p>
                            <p className="text-sm mt-2 text-gray-600 dark:text-gray-400">or create a new plan to get started</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Plan Modal */}
            {showCreateModal && (
                <CreatePlanModal
                    planName={planName}
                    setPlanName={setPlanName}
                    selectedCountry={selectedCountry}
                    selectedYear={selectedYear}
                    onYearChange={handleYearChange}
                    countryHolidays={countryHolidays}
                    setCountryHolidays={setCountryHolidays}
                    selectedHolidays={selectedHolidays}
                    setSelectedHolidays={setSelectedHolidays}
                    loading={loading}
                    onCountrySelect={handleCountrySelect}
                    onToggleHoliday={toggleHolidaySelection}
                    onConfirm={handleCreatePlan}
                    onCancel={() => {
                        setShowCreateModal(false);
                        setPlanName('');
                        setSelectedCountry(null);
                        setCountryHolidays([]);
                        setSelectedHolidays([]);
                    }}
                />
            )}

            {/* Employee Assignment Modal */}
            {showEmployeeModal && (
                <EmployeeAssignmentModal
                    employees={filteredEmployees}
                    selectedEmployees={selectedEmployees}
                    setSelectedEmployees={setSelectedEmployees}
                    filters={employeeFilters}
                    setFilters={setEmployeeFilters}
                    onConfirm={handleAssignEmployees}
                    onCancel={() => setShowEmployeeModal(false)}
                />
            )}

            {/* Create Year Modal */}
            {showCreateYearModal && (
                <CreateYearModal
                    year={newYearToCreate}
                    onYearChange={setNewYearToCreate}
                    existingYears={planYears}
                    sourceYear={selectedPlanYear || selectedPlan?.year}
                    countryCode={selectedPlan?.country_code}
                    countryName={selectedPlan?.country_name}
                    onConfirm={handleCreateYearHolidays}
                    onCancel={() => setShowCreateYearModal(false)}
                    loading={loading}
                />
            )}
        </div>
    );
};

// Plan Details View Component
const PlanDetailsView = ({
    plan, planYears = [], selectedYear, onYearChange, onAddYear, onDeleteYear,
    availableHolidaysForYear = [],
    onAddHoliday, onUpdateHoliday, onDeleteHoliday, onAssignEmployees, onExcelImport, loading
}) => {
    const [showAddHoliday, setShowAddHoliday] = useState(false);
    const [showAvailableHolidays, setShowAvailableHolidays] = useState(false);
    const [newHoliday, setNewHoliday] = useState({
        name: '',
        date: '',
        isOptional: false,
        isSpecial: false
    });
    const [localHolidays, setLocalHolidays] = useState(plan.holidays || []);

    // Update local holidays when plan changes
    useEffect(() => {
        setLocalHolidays(plan.holidays || []);
    }, [plan.holidays]);

    // Show available holidays panel when holidays are fetched for a year
    useEffect(() => {
        if (availableHolidaysForYear.length > 0) {
            setShowAvailableHolidays(true);
        }
    }, [availableHolidaysForYear]);

    const handleLocalUpdate = async (holidayId, updates) => {
        // Optimistic update
        setLocalHolidays(prev => prev.map(h =>
            h.id === holidayId ? { ...h, is_optional: updates.isOptional, is_special: updates.isSpecial } : h
        ));

        // Format date properly to avoid timezone issues
        let formattedDate = updates.date;
        if (updates.date) {
            // If date is already a string in YYYY-MM-DD format, use it as-is
            if (typeof updates.date === 'string' && updates.date.match(/^\d{4}-\d{2}-\d{2}/)) {
                formattedDate = updates.date.split('T')[0]; // Get just the date part
            } else {
                // Otherwise, format it
                const dateObj = new Date(updates.date);
                formattedDate = dateObj.toISOString().split('T')[0];
            }
        }

        // Call the actual update with formatted data
        await onUpdateHoliday(holidayId, {
            ...updates,
            date: formattedDate
        });
    };

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{plan.name}</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {countries.find(c => c.code === plan.country_code)?.flag} {plan.country_name}
                    </p>
                </div>
                <button
                    onClick={onAssignEmployees}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
                >
                    <Users className="w-4 h-4" />
                    Assign Employees ({plan.employees?.length || 0})
                </button>
            </div>

            {/* Year Tabs */}
            <div className="flex items-center gap-2 mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">
                <div className="flex items-center gap-1 flex-wrap">
                    {planYears.sort((a, b) => a - b).map(year => (
                        <button
                            key={year}
                            onClick={() => onYearChange(year)}
                            disabled={loading}
                            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors relative group ${
                                selectedYear === year
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                            }`}
                        >
                            {year}
                            {/* Delete button - only show on hover and if not selected or multiple years exist */}
                            {planYears.length > 1 && (
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteYear(year);
                                    }}
                                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                    title={`Delete ${year}`}
                                >
                                    ×
                                </span>
                            )}
                        </button>
                    ))}
                    {/* Add Year Button */}
                    <button
                        onClick={onAddYear}
                        disabled={loading}
                        className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 flex items-center gap-1"
                        title="Add holidays for a new year"
                    >
                        <Plus className="w-4 h-4" />
                    </button>
                </div>
                {loading && <span className="text-sm text-gray-500 ml-2">Loading...</span>}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 mb-4">
                <button
                    onClick={() => setShowAddHoliday(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" />
                    Add Holiday
                </button>
                <label className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-2 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Import from Excel
                    <input type="file" accept=".xlsx,.xls" onChange={onExcelImport} className="hidden" />
                </label>
            </div>

            {/* Available Holidays for Selected Year - shown after year change */}
            {showAvailableHolidays && availableHolidaysForYear.length > 0 && (
                <div className="mb-4 border border-blue-200 dark:border-blue-700 rounded-lg bg-blue-50 dark:bg-blue-900/20 p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                            Available Holidays for {selectedYear} ({availableHolidaysForYear.length} found)
                        </h3>
                        <button
                            onClick={() => setShowAvailableHolidays(false)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-blue-100 dark:bg-blue-800/30">
                                <tr>
                                    <th className="px-3 py-1 text-left text-blue-700 dark:text-blue-300">Holiday</th>
                                    <th className="px-3 py-1 text-left text-blue-700 dark:text-blue-300">Date</th>
                                    <th className="px-3 py-1 text-center text-blue-700 dark:text-blue-300">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-200 dark:divide-blue-700">
                                {availableHolidaysForYear.map((holiday, idx) => {
                                    // Check if this holiday is already in the plan
                                    const alreadyExists = localHolidays.some(h =>
                                        h.date?.split('T')[0] === holiday.date?.split('T')[0]
                                    );
                                    return (
                                        <tr key={idx} className="hover:bg-blue-100 dark:hover:bg-blue-800/30">
                                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{holiday.name}</td>
                                            <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                                {formatISTDate(holiday.date, { year: 'numeric', month: 'short', day: 'numeric' })}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                {alreadyExists ? (
                                                    <span className="text-gray-400 text-xs">Already added</span>
                                                ) : (
                                                    <button
                                                        onClick={() => {
                                                            onAddHoliday({
                                                                name: holiday.name,
                                                                date: holiday.date,
                                                                isOptional: holiday.isOptional || false,
                                                                isSpecial: holiday.isSpecial || false
                                                            });
                                                        }}
                                                        className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                                    >
                                                        Add
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Holidays Table */}
            <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Holiday Name</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Date</th>
                            <th className="px-4 py-2 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Optional</th>
                            <th className="px-4 py-2 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Special</th>
                            <th className="px-4 py-2 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                        {localHolidays?.map(holiday => (
                            <tr key={holiday.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{holiday.name}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                    {formatISTDate(holiday.date, { year: 'numeric', month: 'short', day: 'numeric' })} ({holiday.day_of_week})
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <input
                                        type="checkbox"
                                        checked={holiday.is_optional || false}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleLocalUpdate(holiday.id, {
                                                name: holiday.name,
                                                date: holiday.date,
                                                isOptional: e.target.checked,
                                                isSpecial: holiday.is_special || false
                                            });
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-4 h-4 cursor-pointer"
                                    />
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <input
                                        type="checkbox"
                                        checked={holiday.is_special || false}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleLocalUpdate(holiday.id, {
                                                name: holiday.name,
                                                date: holiday.date,
                                                isOptional: holiday.is_optional || false,
                                                isSpecial: e.target.checked
                                            });
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-4 h-4 cursor-pointer"
                                    />
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <button
                                        onClick={() => onDeleteHoliday(holiday.id)}
                                        className="text-red-600 hover:text-red-800"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Add Holiday Form */}
            {showAddHoliday && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96">
                        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Add Holiday</h3>
                        <input
                            type="text"
                            placeholder="Holiday Name"
                            value={newHoliday.name}
                            onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded mb-3 dark:bg-gray-700 dark:text-white"
                        />
                        <input
                            type="date"
                            value={newHoliday.date}
                            onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded mb-3 dark:bg-gray-700 dark:text-white"
                        />
                        <div className="flex gap-4 mb-4">
                            <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={newHoliday.isOptional}
                                    onChange={(e) => setNewHoliday({ ...newHoliday, isOptional: e.target.checked })}
                                />
                                Optional
                            </label>
                            <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={newHoliday.isSpecial}
                                    onChange={(e) => setNewHoliday({ ...newHoliday, isSpecial: e.target.checked })}
                                />
                                Special
                            </label>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    onAddHoliday(newHoliday);
                                    setShowAddHoliday(false);
                                    setNewHoliday({ name: '', date: '', isOptional: false, isSpecial: false });
                                }}
                                className="flex-1 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                                Add
                            </button>
                            <button
                                onClick={() => setShowAddHoliday(false)}
                                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Create Plan Modal Component
const CreatePlanModal = ({
    planName, setPlanName, selectedCountry, selectedYear, onYearChange,
    countryHolidays, setCountryHolidays, selectedHolidays, setSelectedHolidays,
    loading, onCountrySelect, onToggleHoliday, onConfirm, onCancel
}) => {
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');
    const [showAddHoliday, setShowAddHoliday] = useState(false);
    const [newHoliday, setNewHoliday] = useState({
        name: '',
        date: '',
        isOptional: false,
        isSpecial: false
    });

    const filteredCountries = countries.filter(c =>
        c.name.toLowerCase().includes(countrySearch.toLowerCase())
    );

    const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i);

    const handleAddHoliday = () => {
        if (!newHoliday.name || !newHoliday.date) {
            alert('Please enter holiday name and date');
            return;
        }

        const holiday = {
            ...newHoliday,
            dayOfWeek: (() => {
                // Parse date string directly to avoid UTC timezone shift
                // Create at noon IST (06:30 UTC) to ensure correct day
                const [year, month, day] = newHoliday.date.split('-').map(Number);
                const dateObj = new Date(Date.UTC(year, month - 1, day, 6, 30, 0));
                return dateObj.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'Asia/Kolkata' });
            })()
        };

        onToggleHoliday(holiday);
        setNewHoliday({ name: '', date: '', isOptional: false, isSpecial: false });
        setShowAddHoliday(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Create Holiday Plan</h2>

                    {/* Step 1: Plan Name */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Plan Name</label>
                        <input
                            type="text"
                            value={planName}
                            onChange={(e) => setPlanName(e.target.value)}
                            placeholder="Enter plan name"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                        />
                    </div>

                    {/* Step 2: Country Selection */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Select Country</label>
                        <div className="relative">
                            <button
                                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-between dark:bg-gray-700 dark:text-white"
                            >
                                {selectedCountry ? (
                                    <span>{selectedCountry.flag} {selectedCountry.name}</span>
                                ) : (
                                    <span className="text-gray-500 dark:text-gray-400">Choose a country</span>
                                )}
                                <ChevronDown className="w-4 h-4" />
                            </button>

                            {showCountryDropdown && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                                    <div className="p-2 border-b border-gray-200 dark:border-gray-600">
                                        <input
                                            type="text"
                                            placeholder="Search country..."
                                            value={countrySearch}
                                            onChange={(e) => setCountrySearch(e.target.value)}
                                            className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-600 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                                        />
                                    </div>
                                    {filteredCountries.map(country => (
                                        <button
                                            key={country.code}
                                            onClick={() => {
                                                onCountrySelect(country);
                                                setShowCountryDropdown(false);
                                            }}
                                            className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 text-gray-900 dark:text-white"
                                        >
                                            <span className="text-xl">{country.flag}</span>
                                            <span>{country.name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Step 3: Year Selection - Re-fetches holidays when changed */}
                    {selectedCountry && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Select Year</label>
                            <select
                                value={selectedYear}
                                onChange={(e) => onYearChange(parseInt(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                                disabled={loading}
                            >
                                {years.map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                            {loading && <p className="text-sm text-gray-500 mt-1">Loading holidays...</p>}
                        </div>
                    )}

                    {/* Step 4: Select Holidays from API or Add Manually */}
                    {selectedCountry && (
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Holidays ({selectedHolidays.length} selected)
                                </label>
                                <button
                                    onClick={() => setShowAddHoliday(true)}
                                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 flex items-center gap-1"
                                >
                                    <Plus className="w-3 h-3" />
                                    Add Custom Holiday
                                </button>
                            </div>

                            {/* Fetched Holidays from API */}
                            {countryHolidays.length > 0 && (
                                <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-64 overflow-y-auto mb-3">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-2 w-12"></th>
                                                <th className="px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300">Holiday Name</th>
                                                <th className="px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300">Date</th>
                                                <th className="px-4 py-2 text-center text-sm text-gray-700 dark:text-gray-300">Optional</th>
                                                <th className="px-4 py-2 text-center text-sm text-gray-700 dark:text-gray-300">Special</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                            {countryHolidays.map((holiday, idx) => {
                                                const selectedHoliday = selectedHolidays.find(h => h.date === holiday.date);
                                                const isOptional = selectedHoliday ? selectedHoliday.isOptional : holiday.isOptional;
                                                const isSpecial = selectedHoliday ? selectedHoliday.isSpecial : holiday.isSpecial;

                                                return (
                                                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                                                        <td className="px-4 py-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedHolidays.some(h => h.date === holiday.date)}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    onToggleHoliday({ ...holiday, isOptional, isSpecial });
                                                                }}
                                                                className="w-4 h-4"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{holiday.name}</td>
                                                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                            {formatISTDate(holiday.date, { year: 'numeric', month: 'short', day: 'numeric' })} ({holiday.dayOfWeek})
                                                        </td>
                                                        <td className="px-4 py-2 text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={isOptional || false}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    const updated = { ...holiday, isOptional: e.target.checked, isSpecial };
                                                                    // Update in selected holidays if already selected
                                                                    if (selectedHolidays.some(h => h.date === holiday.date)) {
                                                                        setSelectedHolidays(prev => prev.map(h =>
                                                                            h.date === holiday.date ? updated : h
                                                                        ));
                                                                    } else {
                                                                        // Update in countryHolidays only
                                                                        setCountryHolidays(prev => prev.map((h, i) =>
                                                                            i === idx ? updated : h
                                                                        ));
                                                                    }
                                                                }}
                                                                className="w-4 h-4 cursor-pointer"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-2 text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSpecial || false}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    const updated = { ...holiday, isOptional, isSpecial: e.target.checked };
                                                                    // Update in selected holidays if already selected
                                                                    if (selectedHolidays.some(h => h.date === holiday.date)) {
                                                                        setSelectedHolidays(prev => prev.map(h =>
                                                                            h.date === holiday.date ? updated : h
                                                                        ));
                                                                    } else {
                                                                        // Update in countryHolidays only
                                                                        setCountryHolidays(prev => prev.map((h, i) =>
                                                                            i === idx ? updated : h
                                                                        ));
                                                                    }
                                                                }}
                                                                className="w-4 h-4 cursor-pointer"
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Selected Holidays Summary */}
                            {selectedHolidays.length > 0 && (
                                <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-64 overflow-y-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300">Holiday Name</th>
                                                <th className="px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300">Date</th>
                                                <th className="px-4 py-2 text-center text-sm text-gray-700 dark:text-gray-300">Optional</th>
                                                <th className="px-4 py-2 text-center text-sm text-gray-700 dark:text-gray-300">Special</th>
                                                <th className="px-4 py-2 text-center text-sm text-gray-700 dark:text-gray-300">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                            {selectedHolidays.map((holiday, idx) => (
                                                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-600">
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{holiday.name}</td>
                                                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                                                        {formatISTDate(holiday.date, { year: 'numeric', month: 'short', day: 'numeric' })} ({holiday.dayOfWeek})
                                                    </td>
                                                    <td className="px-4 py-2 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={holiday.isOptional || false}
                                                            onChange={(e) => {
                                                                setSelectedHolidays(prev => prev.map(h =>
                                                                    h.date === holiday.date
                                                                        ? { ...h, isOptional: e.target.checked }
                                                                        : h
                                                                ));
                                                            }}
                                                            className="w-4 h-4 cursor-pointer"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-2 text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={holiday.isSpecial || false}
                                                            onChange={(e) => {
                                                                setSelectedHolidays(prev => prev.map(h =>
                                                                    h.date === holiday.date
                                                                        ? { ...h, isSpecial: e.target.checked }
                                                                        : h
                                                                ));
                                                            }}
                                                            className="w-4 h-4 cursor-pointer"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-2 text-center">
                                                        <button
                                                            onClick={() => onToggleHoliday(holiday)}
                                                            className="text-red-600 hover:text-red-800"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Add Holiday Form Modal */}
                    {showAddHoliday && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96">
                                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Add Holiday</h3>
                                <input
                                    type="text"
                                    placeholder="Holiday Name"
                                    value={newHoliday.name}
                                    onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded mb-3 dark:bg-gray-700 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                                />
                                <input
                                    type="date"
                                    value={newHoliday.date}
                                    onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded mb-3 dark:bg-gray-700 dark:text-white"
                                />
                                <div className="flex gap-4 mb-4">
                                    <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={newHoliday.isOptional}
                                            onChange={(e) => setNewHoliday({ ...newHoliday, isOptional: e.target.checked })}
                                        />
                                        Optional
                                    </label>
                                    <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={newHoliday.isSpecial}
                                            onChange={(e) => setNewHoliday({ ...newHoliday, isSpecial: e.target.checked })}
                                        />
                                        Special
                                    </label>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleAddHoliday}
                                        className="flex-1 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                    >
                                        Add
                                    </button>
                                    <button
                                        onClick={() => setShowAddHoliday(false)}
                                        className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Old Holiday Selection Section - Remove */}
                    {countryHolidays.length > 0 && false && (
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium">
                                    Select Holidays ({selectedHolidays.length} selected)
                                </label>
                            </div>
                            <div className="border rounded-lg max-h-64 overflow-y-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 sticky top-0">
                                        <tr>
                                            <th className="px-4 py-2 w-12"></th>
                                            <th className="px-4 py-2 text-left text-sm">Holiday Name</th>
                                            <th className="px-4 py-2 text-left text-sm">Date</th>
                                            <th className="px-4 py-2 text-center text-sm">Optional</th>
                                            <th className="px-4 py-2 text-center text-sm">Special</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {countryHolidays.map((holiday, idx) => (
                                            <tr key={idx} className="hover:bg-gray-50">
                                                <td className="px-4 py-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedHolidays.some(h => h.date === holiday.date)}
                                                        onChange={() => onToggleHoliday(holiday)}
                                                        className="w-4 h-4"
                                                    />
                                                </td>
                                                <td className="px-4 py-2 text-sm">{holiday.name}</td>
                                                <td className="px-4 py-2 text-sm">
                                                    {formatISTDate(holiday.date, { year: 'numeric', month: 'short', day: 'numeric' })} ({holiday.dayOfWeek})
                                                </td>
                                                <td className="px-4 py-2 text-center">
                                                    <input type="checkbox" checked={holiday.isOptional} readOnly className="w-4 h-4" />
                                                </td>
                                                <td className="px-4 py-2 text-center">
                                                    <input type="checkbox" checked={holiday.isSpecial} readOnly className="w-4 h-4" />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 mt-6">
                        <button
                            onClick={onConfirm}
                            disabled={loading || !planName || !selectedCountry || selectedHolidays.length === 0}
                            className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            <Check className="w-4 h-4" />
                            Confirm ({selectedHolidays.length} holidays)
                        </button>
                        <button
                            onClick={onCancel}
                            className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Employee Assignment Modal Component
const EmployeeAssignmentModal = ({
    employees, selectedEmployees, setSelectedEmployees,
    filters, setFilters, onConfirm, onCancel
}) => {
    const [showFilters, setShowFilters] = useState(false);

    const toggleEmployee = (empId) => {
        setSelectedEmployees(prev =>
            prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
        );
    };

    const toggleAll = () => {
        if (selectedEmployees.length === employees.length) {
            setSelectedEmployees([]);
        } else {
            setSelectedEmployees(employees.map(e => e.id));
        }
    };

    // Get unique departments
    const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Assign Employees to Holiday Plan</h2>

                    {/* Filter Button */}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="mb-4 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                    >
                        <Filter className="w-4 h-4" />
                        Filters
                        {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>

                    {/* Filters */}
                    {showFilters && (
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <select
                                value={filters.department}
                                onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                            >
                                <option value="">All Departments</option>
                                {departments.map(dept => (
                                    <option key={dept} value={dept}>{dept}</option>
                                ))}
                            </select>
                            <select
                                value={filters.location}
                                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                            >
                                <option value="">All Locations</option>
                                <option value="Office">Office</option>
                                <option value="Remote">Remote</option>
                            </select>
                        </div>
                    )}

                    {/* Select All */}
                    <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={selectedEmployees.length === employees.length && employees.length > 0}
                                onChange={toggleAll}
                                className="w-4 h-4"
                            />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Select All ({employees.length})</span>
                        </label>
                        <span className="text-sm text-gray-600 dark:text-gray-400">{selectedEmployees.length} selected</span>
                    </div>
                </div>

                {/* Employee List */}
                <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
                    <div className="space-y-2">
                        {employees.map(emp => (
                            <label
                                key={emp.id}
                                className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-white dark:hover:bg-gray-800 cursor-pointer bg-white dark:bg-gray-800"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedEmployees.includes(emp.id)}
                                    onChange={() => toggleEmployee(emp.id)}
                                    className="w-4 h-4"
                                />
                                <div className="flex-1">
                                    <p className="font-medium text-sm text-gray-900 dark:text-white">{emp.name || `${emp.first_name} ${emp.last_name}`}</p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">{emp.department} • {emp.designation}</p>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Actions */}
                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex gap-2">
                    <button
                        onClick={onConfirm}
                        className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                    >
                        <Check className="w-4 h-4" />
                        Assign ({selectedEmployees.length} employees)
                    </button>
                    <button
                        onClick={onCancel}
                        className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// Optional Holiday Requests Panel Component
const OptionalHolidayRequestsPanel = ({
    requests, filter, onFilterChange, onApprove, onReject, onClose, processingId
}) => {
    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 rounded-full text-xs">
                        <Clock className="w-3 h-3" />
                        Pending
                    </span>
                );
            case 'approved':
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full text-xs">
                        <CheckCircle className="w-3 h-3" />
                        Approved
                    </span>
                );
            case 'rejected':
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 rounded-full text-xs">
                        <XCircle className="w-3 h-3" />
                        Rejected
                    </span>
                );
            default:
                return null;
        }
    };

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Optional Holiday Requests</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Review and manage employee requests for optional holidays
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 mb-4">
                {['pending', 'approved', 'rejected', 'all'].map((status) => (
                    <button
                        key={status}
                        onClick={() => onFilterChange(status)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            filter === status
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                        }`}
                    >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                        {status === 'pending' && requests.filter(r => r.status === 'pending').length > 0 && (
                            <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">
                                {requests.filter(r => r.status === 'pending').length}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Requests Table */}
            {requests.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                    <p>No {filter !== 'all' ? filter : ''} requests found</p>
                </div>
            ) : (
                <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Employee</th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Holiday</th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Date</th>
                                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Requested On</th>
                                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                            {requests.map((request) => (
                                <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-4 py-3">
                                        <div>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                {request.employee_name}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {request.employee_code} • {request.department_name || 'No Dept'}
                                            </p>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        {request.holiday_name}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                        {formatISTDate(request.holiday_date, { year: 'numeric', month: 'short', day: 'numeric' })}
                                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                                            ({request.day_of_week})
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {getStatusBadge(request.status)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                        {formatISTDate(request.requested_at, { year: 'numeric', month: 'short', day: 'numeric' })}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {request.status === 'pending' ? (
                                            <div className="flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() => onApprove(request.id)}
                                                    disabled={processingId === request.id}
                                                    className="p-1.5 bg-green-100 text-green-600 rounded hover:bg-green-200 disabled:opacity-50"
                                                    title="Approve"
                                                >
                                                    <Check className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => onReject(request.id)}
                                                    disabled={processingId === request.id}
                                                    className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 disabled:opacity-50"
                                                    title="Reject"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {request.reviewed_by_name && `by ${request.reviewed_by_name}`}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Rejection reason display for rejected requests */}
            {filter === 'rejected' && requests.some(r => r.rejection_reason) && (
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rejection Reasons</h4>
                    <div className="space-y-2">
                        {requests.filter(r => r.rejection_reason).map(r => (
                            <div key={r.id} className="text-sm text-gray-600 dark:text-gray-400">
                                <span className="font-medium">{r.employee_name}:</span> {r.rejection_reason}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Create Year Modal Component
const CreateYearModal = ({
    year, onYearChange, existingYears = [], sourceYear, countryCode, countryName,
    onConfirm, onCancel, loading
}) => {
    // Use the currently selected year as the source for copying
    // Fallback to max of existing years, or the passed sourceYear
    const copySourceYear = sourceYear || (existingYears.length > 0 ? Math.max(...existingYears) : null);

    // Default to 'copy' if there's a source year, otherwise 'fetch'
    const [selectedMethod, setSelectedMethod] = useState(copySourceYear ? 'copy' : 'fetch');

    // Generate available years (next 5 years that don't exist yet)
    const availableYears = [];
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y <= currentYear + 5; y++) {
        if (!existingYears.includes(y)) {
            availableYears.push(y);
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
                <div className="p-6">
                    <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                        Add Holidays for New Year
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Create holidays for a new year in this plan
                    </p>

                    {/* Year Selection */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                            Select Year
                        </label>
                        <select
                            value={year}
                            onChange={(e) => onYearChange(parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
                        >
                            {availableYears.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>

                    {/* Method Selection */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300">
                            How would you like to create holidays?
                        </label>
                        <div className="space-y-2">
                            {/* Copy from current year - FIRST OPTION when source year exists */}
                            {copySourceYear && (
                                <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                                    selectedMethod === 'copy'
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                                }`}>
                                    <input
                                        type="radio"
                                        name="method"
                                        value="copy"
                                        checked={selectedMethod === 'copy'}
                                        onChange={() => setSelectedMethod('copy')}
                                        className="mt-1"
                                    />
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <Copy className="w-4 h-4 text-green-600" />
                                            <span className="font-medium text-gray-900 dark:text-white">Copy from {copySourceYear}</span>
                                            <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded">Recommended</span>
                                        </div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                            Copy all holidays from {copySourceYear} with dates updated to {year}
                                        </p>
                                    </div>
                                </label>
                            )}

                            {/* Fetch from API */}
                            <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                                selectedMethod === 'fetch'
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                            }`}>
                                <input
                                    type="radio"
                                    name="method"
                                    value="fetch"
                                    checked={selectedMethod === 'fetch'}
                                    onChange={() => setSelectedMethod('fetch')}
                                    className="mt-1"
                                />
                                <div>
                                    <div className="flex items-center gap-2">
                                        <RefreshCw className="w-4 h-4 text-blue-600" />
                                        <span className="font-medium text-gray-900 dark:text-white">Fetch from holiday API</span>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        Get official holidays for {countryName || 'the country'} in {year}
                                    </p>
                                </div>
                            </label>

                            {/* Start empty */}
                            <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                                selectedMethod === 'empty'
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                            }`}>
                                <input
                                    type="radio"
                                    name="method"
                                    value="empty"
                                    checked={selectedMethod === 'empty'}
                                    onChange={() => setSelectedMethod('empty')}
                                    className="mt-1"
                                />
                                <div>
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-gray-600" />
                                        <span className="font-medium text-gray-900 dark:text-white">Start with empty list</span>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        Add holidays manually one by one
                                    </p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => onConfirm(selectedMethod)}
                            disabled={loading}
                            className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4" />
                                    Create {year}
                                </>
                            )}
                        </button>
                        <button
                            onClick={onCancel}
                            disabled={loading}
                            className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HolidayPlanManagement;
